import { useCallback, useEffect } from 'react';
import { isEqual } from 'lodash';

import { DischargeSteps } from 'components/dischargeForm/helpers';
import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput, { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import Alert from 'components/shared/Alert';
import Checkbox from 'components/shared/form/Checkbox';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import { useForm } from 'context/form';
import { useTranslation } from 'context/translations';
import DischargeReason from 'models/DischargeReason';
import Group from 'models/Group';
import LocationEpisode from 'models/LocationEpisode';
import { useInfiniteDischargeReasons } from 'services/api/dischargeReasons';
import { useInfiniteGroups } from 'services/api/group';
import { colors } from 'styles/theme/colors';

type Props = {
  locationEpisode: LocationEpisode;
  showActualDischargeDate?: boolean;
  hideDischargedAma?: boolean;
};

export type DischargeFormValues = {
  id: string;
  dischargeReason: DischargeReason;
  dischargedGroup: Group;
  dischargedLocationOther: string;
  againstMedicalAdvice: boolean;
  actualDischargeDate: Date;
  note: ActivityInputValues;
  questions: Record<string, string>;
};

export type DischargeFormMeta = {
  step: DischargeSteps;
  isSubmitting: boolean;
  isUploadingAttachments: boolean;
};

export default function DischargeInformation({
  locationEpisode,
  showActualDischargeDate = false,
  hideDischargedAma = false,
}: Props) {
  const { initialValues, values, setValue, setMeta, errors, validateField } = useForm<
    DischargeFormValues,
    DischargeFormMeta
  >();
  const { note, setNote } = useActivityInput({ ...values.note });
  const { translate } = useTranslation();

  const asyncDischargeReasons = useInfiniteDischargeReasons({
    include: 'dischargedToGroupType',
    sortBy: 'order asc',
    groupType: locationEpisode.group?.groupTypeId,
  });

  const selectedReason = values.dischargeReason;

  const asyncGroupOptions = useInfiniteGroups({
    clientIdOrNetworkClientId: selectedReason?.dischargedToGroupType?.isManager
      ? undefined
      : locationEpisode.owner.clientId,
    groupType: selectedReason?.dischargedToGroupTypeId ?? undefined,
    'id.not': locationEpisode.group?.id,
    search: '',
    sortBy: 'name asc',
    viewOnly: true,
  });

  const isOtherLocation = values.dischargedGroup?.name === 'Other';
  const enteredAt = locationEpisode.currentRehabState.enteredAt;
  const isReadmission = selectedReason?.isReadmission;
  const notesLabel = isReadmission ? 'Readmission Details' : 'Notes (optional)';
  const selectedReasonInfoMessage = selectedReason?.infoMessage;
  const selectedReasonWarningMessage = selectedReason?.warningMessage;

  useEffect(() => {
    setValue('note', note);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note]);

  const setDischargeFormValue = useCallback(
    (...args: Parameters<typeof setValue>) => {
      // If the value id matches the initial value id, use the initial value object
      // so that the form isn't dirty when the user selects the same value
      if (args[1]?.id && isEqual(args[1]?.id, initialValues[args[0]]?.id)) {
        args[1] = initialValues[args[0]];
      }

      setValue(...args);
    },
    [initialValues, setValue]
  );

  return (
    <>
      {showActualDischargeDate && (
        <InputGroup title={`Actual ${translate('Discharge')} Date`} error={errors.actualDischargeDate}>
          <DatePicker
            minDate={enteredAt ?? new Date()}
            maxDate={new Date()}
            selected={values.actualDischargeDate ?? null}
            onChange={(z) => {
              setValue('actualDischargeDate', z);
            }}
            $hasError={!!errors.actualDischargeDate}
            onBlur={() => validateField('actualDischargeDate', values)}
          />
        </InputGroup>
      )}
      <InputGroup title={`${translate('Discharged')} To`} error={errors.dischargeReason} data-cy='dischargeReason'>
        <Select<DischargeReason>
          {...asyncDischargeReasons}
          placeholder='Select'
          onChange={(change) => {
            setValue('dischargedGroup', null, { validate: false });
            setValue('dischargedLocationOther', '', { validate: false });
            setDischargeFormValue('dischargeReason', change);
          }}
          getOptionValue={(prop) => prop.id}
          getOptionLabel={(prop) => prop.displayName}
          value={values.dischargeReason}
        />
        {selectedReasonInfoMessage && (
          <Alert style={{ marginTop: 10 }}>
            <Alert.Text>{selectedReasonInfoMessage}</Alert.Text>
          </Alert>
        )}
      </InputGroup>
      {!hideDischargedAma && (
        <InputGroup>
          <Checkbox
            checked={values.againstMedicalAdvice || false}
            checkedColor={colors.primaryBlue}
            size={16}
            labelSize='14px'
            label={`${translate('Discharged')} against medical advice (AMA)`}
            onChange={(val) => setValue('againstMedicalAdvice', val)}
          />
        </InputGroup>
      )}
      <InputGroup
        title={selectedReason?.dischargedToGroupType?.displayName}
        visible={!!selectedReason?.dischargedToGroupTypeId}
        error={errors.dischargedGroup}>
        <Select
          {...asyncGroupOptions}
          appendedOptions={[new Group({ name: 'Other' })]}
          placeholder='Select a location'
          getOptionValue={(prop) => prop.id}
          getOptionLabel={(prop) => prop.name}
          onChange={(change) => {
            setValue('dischargedLocationOther', '', { validate: false });
            setDischargeFormValue('dischargedGroup', change);
          }}
          value={values.dischargedGroup}
        />
      </InputGroup>
      <InputGroup title='Other' visible={isOtherLocation} error={errors.dischargedLocationOther}>
        <Input
          value={values.dischargedLocationOther ?? ''}
          onChange={(event) => setValue('dischargedLocationOther', event.target.value)}
          $hasError={false}
          disabled={false}
        />
      </InputGroup>
      <FieldLabel>{notesLabel}</FieldLabel>
      <ActivityInput
        locationEpisodeId={locationEpisode.id}
        values={note}
        setValues={setNote}
        onUploading={(uploading) => setMeta('isUploadingAttachments', uploading)}
      />
      {selectedReasonWarningMessage && (
        <Alert style={{ marginTop: 10 }}>
          <Alert.Text>{selectedReasonWarningMessage}</Alert.Text>
        </Alert>
      )}
    </>
  );
}
